import React from 'react';
import { X, ShoppingCart, Heart, Trash2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

export const WishlistSheet = ({ isOpen, onClose, wishlistItems, onAddToCart, onRemoveFromWishlist }) => {
  const handleAddToCart = (item) => {
    onAddToCart(item);
    toast.success(`${item.name} added to cart!`);
  };

  const handleRemoveItem = (item) => {
    onRemoveFromWishlist(item.id);
    toast.success(`${item.name} removed from wishlist`);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#FAF9F7] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-seasons text-2xl text-[#2C2C2C]">
            <Heart className="h-6 w-6 text-[#C4B5A0]" />
            My Wishlist
            {wishlistItems.length > 0 && (
              <span className="text-sm font-montserrat text-[#8B7D6B]">({wishlistItems.length} items)</span>
            )}
          </SheetTitle>
        </SheetHeader>

        {wishlistItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <Heart className="h-16 w-16 text-[#D4C4B0] mb-4" />
            <p className="font-montserrat text-[#8B7D6B] mb-6">Your wishlist is empty</p>
            <Button
              className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase"
              onClick={onClose}
            >
              Explore Products
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {wishlistItems.map((item) => (
                  <Card key={item.id} className="bg-white border-none shadow-sm p-4">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-24 h-32 flex-shrink-0 bg-[#F5F3F0] rounded overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                        {item.discount && (
                          <Badge className="absolute top-2 left-2 bg-[#6B4E37] text-white text-xs px-2 py-1">
                            {item.discount}
                          </Badge>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2 mb-2">
                          <div className="flex-1">
                            <p className="text-xs text-[#8B7D6B] font-montserrat tracking-wider uppercase mb-1">
                              {item.category}
                            </p>
                            <h4 className="font-montserrat text-sm text-[#2C2C2C] line-clamp-2 mb-2">
                              {item.name}
                            </h4>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-[#8B7D6B] hover:text-[#6B4E37] flex-shrink-0"
                            onClick={() => handleRemoveItem(item)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-seasons text-lg text-[#2C2C2C]">₹{item.price}</span>
                          {item.originalPrice && (
                            <span className="text-sm font-montserrat text-[#8B7D6B] line-through">
                              ₹{item.originalPrice}
                            </span>
                          )}
                        </div>

                        {/* Add to Cart Button */}
                        <Button
                          size="sm"
                          className="w-full bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat text-xs tracking-wider uppercase"
                          onClick={() => handleAddToCart(item)}
                        >
                          <ShoppingCart className="h-3 w-3 mr-2" />
                          Add to Cart
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>

            {/* Bottom Actions */}
            <div className="border-t border-[#D4C4B0] pt-4">
              <Button
                variant="outline"
                className="w-full border-[#C4B5A0] text-[#C4B5A0] hover:bg-[#F5F3F0] font-montserrat tracking-wider uppercase"
                onClick={onClose}
              >
                Continue Shopping
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};